/**
 * Offline Cache Utility for Tabib Healthcare Platform
 *
 * Provides persistence layer for React Query data and offline action queue.
 * Uses IndexedDB for reliable storage of medical data offline.
 */

const DB_NAME = 'tabib-offline-db';
const DB_VERSION = 1;
const QUERY_CACHE_STORE = 'queryCache';
const PENDING_ACTIONS_STORE = 'pendingActions';

/**
 * Why the most recent replay attempt for a queued action failed.
 *
 * `status` is the HTTP status the server answered with, or `null` when the
 * request never reached a server at all (a `TypeError` from `fetch`). The
 * distinction is load-bearing: it is what decides whether a queued write may
 * ever be discarded — see `recordFailedAttempt`.
 */
export type PendingActionFailure = {
  status: number | null;
  /** Epoch milliseconds, so an exhausted action can be aged in the UI. */
  at: number;
};

// Action types for offline queue
export type PendingAction = {
  id: string;
  type:
    | 'CREATE_APPOINTMENT'
    | 'UPDATE_APPOINTMENT'
    | 'CREATE_HEALTH_METRIC'
    | 'UPDATE_MEDICATION_ADHERENCE';
  payload: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
  /** Absent until a replay attempt has failed at least once. */
  lastFailure?: PendingActionFailure;
};

// Initialize IndexedDB
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store for React Query cache
      if (!db.objectStoreNames.contains(QUERY_CACHE_STORE)) {
        db.createObjectStore(QUERY_CACHE_STORE, { keyPath: 'queryKey' });
      }

      // Store for pending offline actions
      if (!db.objectStoreNames.contains(PENDING_ACTIONS_STORE)) {
        const store = db.createObjectStore(PENDING_ACTIONS_STORE, { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('type', 'type', { unique: false });
      }
    };
  });
}

// ============================================================================
// Query Cache Persistence
// ============================================================================

/**
 * Save query data to IndexedDB
 */
export async function saveQueryToCache(queryKey: string, data: unknown): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUERY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(QUERY_CACHE_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        queryKey,
        data,
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('[OfflineCache] Failed to save query:', error);
  }
}

/**
 * Load query data from IndexedDB
 */
export async function loadQueryFromCache(queryKey: string): Promise<unknown | null> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUERY_CACHE_STORE, 'readonly');
    const store = transaction.objectStore(QUERY_CACHE_STORE);

    const data = await new Promise<unknown | null>((resolve, reject) => {
      const request = store.get(queryKey);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });

    db.close();
    return data;
  } catch (error) {
    console.error('[OfflineCache] Failed to load query:', error);
    return null;
  }
}

/**
 * Delete query from cache
 */
export async function deleteQueryFromCache(queryKey: string): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUERY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(QUERY_CACHE_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(queryKey);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('[OfflineCache] Failed to delete query:', error);
  }
}

/**
 * Clear all cached queries
 */
export async function clearQueryCache(): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUERY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(QUERY_CACHE_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('[OfflineCache] Failed to clear query cache:', error);
  }
}

// ============================================================================
// Pending Actions Queue (Offline Mutations)
// ============================================================================

/**
 * A registration on a browser that implements the Background Sync API. TypeScript's
 * DOM lib does not declare `sync` on `ServiceWorkerRegistration` because the API
 * is not on the standards track, so it is narrowed rather than cast away.
 */
type SyncCapableRegistration = ServiceWorkerRegistration & {
  sync: { register(tag: string): Promise<void> };
};

function supportsBackgroundSync(
  registration: ServiceWorkerRegistration
): registration is SyncCapableRegistration {
  return 'sync' in registration;
}

/**
 * Add action to pending queue for sync when online
 */
export async function addPendingAction(
  action: Omit<PendingAction, 'id' | 'timestamp' | 'retryCount' | 'lastFailure'>
): Promise<string> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ACTIONS_STORE);

    const id = `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const pendingAction: PendingAction = {
      ...action,
      id,
      timestamp: Date.now(),
      retryCount: 0,
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.add(pendingAction);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();

    // Request background sync if available. The action is already durable in
    // IndexedDB at this point, so nothing here may reject: the catch below
    // RETHROWS, and a rethrow would report a successful queue write as a
    // failure to `apiRequest`. Two hazards are handled explicitly:
    //   - `SyncManager` is Chromium-only. The previous form dereferenced
    //     `window.SyncManager` directly, so `'sync' in undefined` threw a
    //     TypeError on Safari and Firefox — i.e. queueing failed outright on
    //     the very browsers that most need the queue.
    //   - `sync.register` itself rejects when the user has disabled background
    //     sync; that is not a reason to lose the write either.
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (supportsBackgroundSync(registration)) {
          await registration.sync.register(`sync-${action.type.toLowerCase().replace('_', '-')}`);
        }
      } catch (error) {
        // `processPendingActions` still drains the queue on the `online` event.
        console.warn('[OfflineCache] Background sync registration unavailable:', error);
      }
    }

    return id;
  } catch (error) {
    console.error('[OfflineCache] Failed to add pending action:', error);
    throw error;
  }
}

/**
 * Get all pending actions
 */
export async function getPendingActions(): Promise<PendingAction[]> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(PENDING_ACTIONS_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_ACTIONS_STORE);

    const actions = await new Promise<PendingAction[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return actions.sort((a, b) => a.timestamp - b.timestamp);
  } catch (error) {
    console.error('[OfflineCache] Failed to get pending actions:', error);
    return [];
  }
}

/**
 * Remove action from pending queue
 */
export async function removePendingAction(id: string): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ACTIONS_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('[OfflineCache] Failed to remove pending action:', error);
  }
}

/**
 * Increment an action's retry counter and record why the attempt failed.
 *
 * The `put` is issued SYNCHRONOUSLY from the `get` request's own `onsuccess`
 * handler rather than after `await`ing it. An IndexedDB transaction is
 * deactivated once control returns to the event loop after an event is
 * dispatched, so a `put` issued from a promise continuation several microtasks
 * later can land on an inactive transaction and throw `TransactionInactiveError`.
 * The previous form did exactly that, which means the retry counter may never
 * have persisted in a real browser at all — the error was swallowed by the
 * catch below, so the queue would have replayed forever with `retryCount` stuck
 * at 0. Keeping both requests in one handler also makes the read-modify-write
 * atomic, which the two-await form was not.
 */
export async function updateActionRetryCount(
  id: string,
  failure: PendingActionFailure
): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ACTIONS_STORE);

    await new Promise<void>((resolve, reject) => {
      const getRequest = store.get(id);
      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => {
        const action = getRequest.result as PendingAction | undefined;
        if (!action) {
          resolve();
          return;
        }
        const putRequest = store.put({
          ...action,
          retryCount: action.retryCount + 1,
          lastFailure: failure,
        });
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
    });

    db.close();
  } catch (error) {
    console.error('[OfflineCache] Failed to update retry count:', error);
  }
}

// ============================================================================
// Sync Management
// ============================================================================

const MAX_RETRY_COUNT = 3;

/**
 * The only statuses on which a queued write may be discarded.
 *
 * Each means the server read the payload and rejected the DATA itself, so
 * replaying the identical body can never succeed and there is nothing to
 * recover. Everything else says nothing about the validity of the write:
 * a 403 is a header the client controls, a 429 is a rate limit, a 5xx is the
 * backend being unwell, and a network failure never reached a server at all.
 */
const CLIENT_DATA_REJECTION_STATUSES: ReadonlySet<number> = new Set([400, 409, 422]);

/**
 * True once an action has spent its replay budget. Such an action is KEPT in
 * storage but is no longer sent — retention without back-off would hammer the
 * server on every reconnect.
 */
export function isRetryExhausted(action: PendingAction): boolean {
  return action.retryCount >= MAX_RETRY_COUNT;
}

/**
 * Queued writes that are still held but will not be replayed again.
 *
 * Exported so the offline UI can surface them: an action that is silently kept
 * is only marginally better than one that is silently deleted. `lastFailure`
 * carries the status and time of the attempt that exhausted the budget.
 */
export async function getExhaustedActions(): Promise<PendingAction[]> {
  return (await getPendingActions()).filter(isRetryExhausted);
}

/**
 * Record a failed replay attempt, discarding the action only when the server
 * has told us the payload itself is unacceptable.
 *
 * Deleting a queued action destroys a patient's write — an appointment booked
 * while offline — with nothing shown to them, so it happens on exactly one
 * condition: the retry budget is spent AND the failure that spent it was a
 * client-data rejection. Anything else is retained indefinitely.
 */
async function recordFailedAttempt(action: PendingAction, status: number | null): Promise<void> {
  const budgetSpent = action.retryCount + 1 >= MAX_RETRY_COUNT;
  if (budgetSpent && status !== null && CLIENT_DATA_REJECTION_STATUSES.has(status)) {
    console.warn(
      '[OfflineCache] Discarding action the server rejected as invalid:',
      action.id,
      status
    );
    await removePendingAction(action.id);
    return;
  }
  await updateActionRetryCount(action.id, { status, at: Date.now() });
}

/**
 * Process pending actions when back online
 */
export async function processPendingActions(): Promise<{ success: number; failed: number }> {
  const actions = await getPendingActions();
  let success = 0;
  let failed = 0;

  for (const action of actions) {
    if (isRetryExhausted(action)) {
      // Kept, not deleted: the budget is spent, but nothing about this write
      // was ever found invalid. No request is issued — see `getExhaustedActions`.
      console.warn(
        '[OfflineCache] Action exhausted its retry budget and is being kept for recovery:',
        action.id,
        action.lastFailure?.status ?? 'unreachable'
      );
      failed++;
      continue;
    }

    try {
      const endpoint = getEndpointForAction(action.type);
      const method = getMethodForAction(action.type);

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          // A replay is a state-changing request, so `apiCsrfProtection`
          // (`server/middleware/csrf.ts:239`) rejects it with a 403 without
          // this header. This module cannot simply call `apiRequest`:
          // `queryClient.ts` imports it, so the reverse import is circular, and
          // `apiRequest` re-queues while offline — a replay would feed the very
          // queue it is draining. It sits below `apiRequest` and sets the
          // header on its own `fetch` (spec 025, plan D3).
          'X-Requested-With': 'fetch',
        },
        body: JSON.stringify(action.payload),
        credentials: 'include',
      });

      if (response.ok) {
        await removePendingAction(action.id);
        success++;
      } else {
        await recordFailedAttempt(action, response.status);
        failed++;
      }
    } catch (error) {
      console.error('[OfflineCache] Failed to process action:', action.id, error);
      await recordFailedAttempt(action, null);
      failed++;
    }
  }

  return { success, failed };
}

function getEndpointForAction(type: PendingAction['type']): string {
  switch (type) {
    case 'CREATE_APPOINTMENT':
    case 'UPDATE_APPOINTMENT':
      return '/api/appointments';
    case 'CREATE_HEALTH_METRIC':
      return '/api/health-metrics';
    case 'UPDATE_MEDICATION_ADHERENCE':
      return '/api/medications/adherence';
    default:
      throw new Error(`Unknown action type: ${type}`);
  }
}

function getMethodForAction(type: PendingAction['type']): string {
  switch (type) {
    case 'CREATE_APPOINTMENT':
    case 'CREATE_HEALTH_METRIC':
      return 'POST';
    case 'UPDATE_APPOINTMENT':
    case 'UPDATE_MEDICATION_ADHERENCE':
      return 'PUT';
    default:
      return 'POST';
  }
}

// ============================================================================
// Network Status Management
// ============================================================================

/**
 * Check if currently online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Register online/offline event listeners
 */
export function registerNetworkListeners(onOnline: () => void, onOffline: () => void): () => void {
  const handleOnline = () => {
    console.warn('[OfflineCache] Back online');
    onOnline();
  };

  const handleOffline = () => {
    console.warn('[OfflineCache] Gone offline');
    onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// ============================================================================
// React Query Persister
// ============================================================================

/**
 * Creates a persister for React Query that uses IndexedDB
 */
export function createOfflinePersister() {
  return {
    persistClient: async (client: unknown) => {
      try {
        await saveQueryToCache('react-query-client', client);
      } catch (error) {
        console.error('[OfflineCache] Failed to persist client:', error);
      }
    },
    restoreClient: async () => {
      try {
        return await loadQueryFromCache('react-query-client');
      } catch (error) {
        console.error('[OfflineCache] Failed to restore client:', error);
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await deleteQueryFromCache('react-query-client');
      } catch (error) {
        console.error('[OfflineCache] Failed to remove client:', error);
      }
    },
  };
}

// Auto-process pending actions when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    processPendingActions().then(({ success, failed }) => {
      if (success > 0 || failed > 0) {
        console.warn(
          `[OfflineCache] Processed pending actions: ${success} success, ${failed} failed`
        );
      }
    });
  });
}
